/**
 * Workspace-aware query hook.
 *
 * Wraps TanStack Query + convexQuery and auto-injects `workspaceId`
 * from the URL-based workspace context (`/$workspaceSlug` routes).
 */

import {
  useQuery,
  type keepPreviousData,
  type UseQueryResult,
} from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useWorkspace } from "@/routes/$workspaceSlug/route";
import type { FunctionReference } from "convex/server";

/**
 * Like `useQuery({ ...convexQuery(fn, args) })` but merges the current
 * workspace's `_id` into `args` automatically.
 *
 * Supports `"skip"` as args to disable the query (same as convexQuery convention).
 *
 * Must be called inside a `/$workspaceSlug/*` route.
 *
 * @example
 * ```ts
 * const { data, isLoading } = useWorkspaceQuery(
 *   api.audit_queries.listAudits,
 *   { status: ["active"] },
 * );
 *
 * // Skip query when auditId is null:
 * const { data } = useWorkspaceQuery(
 *   api.audit_queries.getAuditDetail,
 *   auditId ? { auditId } : "skip",
 * );
 * ```
 */
export function useWorkspaceQuery<
  TResult,
  TArgs extends Record<string, unknown>,
>(
  queryFn: FunctionReference<"query", "public", TArgs, TResult>,
  args: Omit<TArgs, "workspaceId"> | "skip" = {} as Omit<TArgs, "workspaceId">,
  options?: {
    placeholderData?: typeof keepPreviousData;
    enabled?: boolean;
  },
): UseQueryResult<TResult> {
  const { workspace } = useWorkspace();

  const resolvedArgs: unknown =
    args === "skip" ? "skip" : { ...args, workspaceId: workspace._id };

  return useQuery({
    // @ts-expect-error: TypeScript can't resolve ConvexQueryArgsOrSkip conditional type for generic TArgs
    ...convexQuery(queryFn, resolvedArgs),
    ...(options?.placeholderData
      ? { placeholderData: options.placeholderData }
      : {}),
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}
